import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Row = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
`;

const Preview = styled.img`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  object-fit: cover;
  background: #0f172a;
  border: 1px solid var(--outline);
  flex-shrink: 0;
`;

const PreviewPlaceholder = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #0f172a;
  border: 1px solid var(--outline);
  flex-shrink: 0;
`;

const Actions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  flex: 1;
  min-width: 140px;
`;

const ActionButton = styled.button`
  background: #1f2937;
  color: #e2e8f0;
  border: 1px solid var(--outline);
  border-radius: 10px;
  padding: 0.55rem 0.75rem;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  text-align: center;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const HiddenFileInput = styled.input`
  display: none;
`;

const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(3, 7, 18, 0.78);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 70;
  padding: 1.5rem;
`;

const ModalCard = styled.div`
  width: min(420px, 92vw);
  background: #0b0f17;
  color: #f8fafc;
  border-radius: 18px;
  border: 1px solid #1f2937;
  box-shadow: 0 30px 60px rgba(0, 0, 0, 0.45);
  overflow: hidden;
`;

const ModalHeader = styled.div`
  padding: 1rem 1.25rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: 1px solid #1f2937;
`;

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 1.05rem;
`;

const ModalCloseButton = styled.button`
  background: transparent;
  border: none;
  color: #94a3b8;
  font-size: 1.25rem;
  cursor: pointer;
  line-height: 1;
`;

const ModalBody = styled.div`
  padding: 1rem 1.25rem 1.25rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const CameraVideo = styled.video`
  width: 100%;
  aspect-ratio: 3 / 4;
  border-radius: 12px;
  background: #000;
  object-fit: cover;
  transform: scaleX(-1);
`;

const CameraError = styled.p`
  margin: 0;
  color: var(--error);
  font-size: 0.9rem;
`;

const CameraHint = styled.p`
  margin: 0;
  color: #94a3b8;
  font-size: 0.85rem;
`;

const ModalActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.75rem;
`;

const PrimaryButton = styled(ActionButton)`
  background: var(--primary);
  color: var(--onSurface);
  border: none;
`;

type ProfilePhotoInputProps = {
  id?: string;
  preview: string | null;
  onChange: (file: File | null, previewUrl: string | null) => void;
};

const ProfilePhotoInput: React.FC<ProfilePhotoInputProps> = ({ id = 'avatar', preview, onChange }) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
    setCameraReady(false);
  }, []);

  const closeCamera = useCallback(() => {
    stopCamera();
    setCameraOpen(false);
    setCameraError(null);
  }, [stopCamera]);

  const startCamera = useCallback(async () => {
    if (!navigator?.mediaDevices?.getUserMedia) {
      setCameraError('Camera is not available in this browser.');
      return;
    }

    stopCamera();
    setCameraError(null);
    setCameraReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'user' } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
      setCameraReady(true);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Unable to access camera.';
      setCameraError(message || 'Unable to access camera.');
    }
  }, [stopCamera]);

  const openCamera = useCallback(() => {
    setCameraOpen(true);
    setCameraError(null);
  }, []);

  useEffect(() => {
    if (!cameraOpen) return;
    void startCamera();
    return () => {
      stopCamera();
    };
  }, [cameraOpen, startCamera, stopCamera]);

  useEffect(() => () => stopCamera(), [stopCamera]);

  const applyFile = (file: File | null) => {
    onChange(file, file ? URL.createObjectURL(file) : null);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] || null;
    applyFile(file);
    event.target.value = '';
  };

  const capturePhoto = async () => {
    const video = videoRef.current;
    if (!video || !cameraReady || video.videoWidth === 0 || video.videoHeight === 0) {
      setCameraError('Camera is not ready yet. Try again in a moment.');
      return;
    }

    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const context = canvas.getContext('2d');
    if (!context) {
      setCameraError('Unable to capture photo.');
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });

    if (!blob) {
      setCameraError('Unable to capture photo.');
      return;
    }

    const file = new File([blob], `profile-photo-${Date.now()}.jpg`, { type: 'image/jpeg' });
    applyFile(file);
    closeCamera();
  };

  return (
    <>
      <Row>
        {preview ? <Preview src={preview} alt="Profile photo preview" /> : <PreviewPlaceholder aria-hidden />}
        <Actions>
          <ActionButton type="button" onClick={() => fileInputRef.current?.click()}>
            Choose photo
          </ActionButton>
          <ActionButton type="button" onClick={openCamera}>
            Take photo
          </ActionButton>
        </Actions>
        <HiddenFileInput
          ref={fileInputRef}
          id={id}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
        />
      </Row>

      {cameraOpen ? (
        <ModalOverlay onClick={closeCamera}>
          <ModalCard onClick={(event) => event.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby={`${id}-camera-title`}>
            <ModalHeader>
              <ModalTitle id={`${id}-camera-title`}>Take profile photo</ModalTitle>
              <ModalCloseButton type="button" onClick={closeCamera} aria-label="Close camera">
                ×
              </ModalCloseButton>
            </ModalHeader>
            <ModalBody>
              <CameraVideo ref={videoRef} playsInline muted />
              {cameraError ? <CameraError>{cameraError}</CameraError> : null}
              {!cameraError ? <CameraHint>Position your face in the frame, then tap Capture.</CameraHint> : null}
              <ModalActions>
                <ActionButton type="button" onClick={closeCamera}>
                  Cancel
                </ActionButton>
                <PrimaryButton type="button" onClick={() => void capturePhoto()} disabled={!cameraReady}>
                  Capture
                </PrimaryButton>
              </ModalActions>
            </ModalBody>
          </ModalCard>
        </ModalOverlay>
      ) : null}
    </>
  );
};

export default ProfilePhotoInput;
